import { computed, onMounted, ref } from 'vue-demi';
import { use as fegoUse } from '@fegobull/webapi';
import {
  DEFAULT_ADVANCED_CHOICE_KEY,
  DEFAULT_CHOICE_KEY,
  EnrollmentStatus,
  isAdvancedChoiceKey,
  isChoiceKey,
  type AdvancedChoiceKey,
  type ChoiceGroup,
  type ChoiceKey,
  type NupStatusResponse,
} from '@ark/nup-shared';
import { NUP_ACTIVITY_ENDED_VAR } from '../constants/fego-variables';
import { apiClient } from '../../shared/api';
import { getCopy } from '../constants/copy';
import { findAwardedImages, findMissedImage } from '../constants/rewards';
import { useNupLang } from './use-nup-lang';
import { usePageTypeFlag } from './use-page-type';
import {
  DEFAULT_DEPOSIT_URL,
  DEFAULT_PRIZE_URL,
  toLink,
  toLogin,
} from '../utils/link';
import {
  getEnrollBlockToast,
  shouldShowEnrollBtn,
} from '../utils/enroll-guard';
import { showToast, toastApiError } from '../utils/toast';

export interface NupPageLinks {
  rulesUrl?: string;
  depositUrl?: string;
  prizeUrl?: string;
}

export function useNupPage(getLinks: () => NupPageLinks) {
  const { useVariable } = fegoUse;
  const fegoVars = useVariable() as Record<string, string>;
  const { isPC } = usePageTypeFlag();
  const { isEn } = useNupLang();
  const copy = computed(() => getCopy(isEn.value));
  const status = ref<NupStatusResponse | null>(null);
  const loading = ref(true);
  const submitting = ref(false);
  const successVisible = ref(false);
  const choiceVisible = ref(false);
  const confirmVisible = ref(false);
  const choiceSuccessVisible = ref(false);
  const pendingChoiceKey = ref<ChoiceKey>(DEFAULT_CHOICE_KEY);
  const pendingChoiceGroup = ref<ChoiceGroup>('entry');
  const choiceSuccessKey = ref<ChoiceKey>(DEFAULT_CHOICE_KEY);

  const enrollment = computed(() => {
    return status.value && status.value.enrollment
      ? status.value.enrollment
      : null;
  });

  const choiceKey = computed<ChoiceKey | null>(() => {
    return enrollment.value ? enrollment.value.choiceKey : null;
  });

  const advancedChoiceKey = computed<AdvancedChoiceKey | null>(() => {
    return enrollment.value ? enrollment.value.advancedChoiceKey : null;
  });

  const isInProgress = computed(() => {
    const row = enrollment.value;
    return !!(
      row &&
      (row.status === EnrollmentStatus.IN_PROGRESS ||
        row.status === EnrollmentStatus.SETTLING)
    );
  });

  const isAwarded = computed(() => {
    return !!(
      enrollment.value && enrollment.value.status === EnrollmentStatus.AWARDED
    );
  });

  const isBelowThreshold = computed(() => {
    return !!(
      enrollment.value &&
      enrollment.value.status === EnrollmentStatus.BELOW_THRESHOLD
    );
  });

  const depositEnded = computed(() => {
    const row = enrollment.value;
    return !!(row && Date.now() >= row.depositEndTime);
  });

  const inDepositWindow = computed(
    () => isInProgress.value && !depositEnded.value,
  );

  const showCountdown = computed(() => inDepositWindow.value);
  /** 入门赏未选：入金期内可点 4 选 1。 */
  const showChoiceBtn = computed(() => {
    return inDepositWindow.value && !choiceKey.value;
  });
  /** 进阶赏未选：入金期内可点 3 选 1，与入门赏互不影响。 */
  const showAdvancedChoiceBtn = computed(() => {
    return inDepositWindow.value && !advancedChoiceKey.value;
  });
  const showDepositBtn = computed(() => inDepositWindow.value);
  const showAwarded = computed(() => isAwarded.value);
  const showMissed = computed(() => isBelowThreshold.value);
  const showTierList = computed(
    () => !isAwarded.value && !isBelowThreshold.value,
  );

  const awardedImages = computed(() => {
    if (isBelowThreshold.value) {
      return [findMissedImage(isPC.value, isEn.value)];
    }
    const row = enrollment.value;
    return findAwardedImages(
      row ? row.netDepositAmount : null,
      choiceKey.value || DEFAULT_CHOICE_KEY,
      isPC.value,
      isEn.value,
      advancedChoiceKey.value || DEFAULT_ADVANCED_CHOICE_KEY,
    );
  });

  /** 仅未报名或 status=3 再出示报名；已发奖 / 未达标 / 进行中隐藏。 */
  const showEnrollBtn = computed(() => {
    return shouldShowEnrollBtn(
      loading.value,
      !!status.value,
      enrollment.value ? enrollment.value.status : null,
    );
  });

  const depositEndTime = computed(() => {
    return enrollment.value ? enrollment.value.depositEndTime : null;
  });

  const netDepositAmount = computed(() => {
    return enrollment.value ? enrollment.value.netDepositAmount : null;
  });

  const stickyText = computed(() => {
    if (showDepositBtn.value) {
      return copy.value.depositNow;
    }
    return copy.value.enrollNow;
  });

  const showSticky = computed(
    () => showEnrollBtn.value || showDepositBtn.value,
  );

  async function refreshStatus(): Promise<void> {
    try {
      status.value = await apiClient.getStatus();
      fegoVars[NUP_ACTIVITY_ENDED_VAR] = status.value.activityEnded ? '1' : '0';
    } catch (error) {
      toastApiError(error, copy.value.loadFailed, isEn.value);
    } finally {
      loading.value = false;
    }
  }

  async function handleEnroll(): Promise<void> {
    if (submitting.value) {
      return;
    }
    const current = status.value;
    if (!current) {
      return;
    }
    if (!current.loggedIn) {
      toLogin();
      return;
    }
    const blockToast = getEnrollBlockToast(current, isEn.value);
    if (blockToast) {
      showToast(blockToast);
      return;
    }
    submitting.value = true;
    try {
      const next = await apiClient.enroll();
      status.value = {
        ...current,
        canEnroll: false,
        enrollment: next,
      };
      successVisible.value = true;
    } catch (error) {
      toastApiError(error, undefined, isEn.value);
    } finally {
      submitting.value = false;
    }
  }

  /** 确认弹窗提交。按 pendingChoiceGroup 写入入门赏或进阶赏。 */
  async function handleUpdateChoice(nextKey: ChoiceKey): Promise<boolean> {
    const group = pendingChoiceGroup.value;
    if (submitting.value) {
      return false;
    }
    if (group === 'advanced') {
      if (!isAdvancedChoiceKey(nextKey)) {
        return false;
      }
    } else if (!isChoiceKey(nextKey)) {
      return false;
    }
    submitting.value = true;
    try {
      const result = await apiClient.updateChoice(nextKey, group);
      if (status.value && status.value.enrollment) {
        status.value = {
          ...status.value,
          enrollment: {
            ...status.value.enrollment,
            ...(group === 'advanced'
              ? { advancedChoiceKey: result.choiceKey as AdvancedChoiceKey }
              : { choiceKey: result.choiceKey }),
          },
        };
      }
      choiceSuccessKey.value = result.choiceKey;
      confirmVisible.value = false;
      choiceSuccessVisible.value = true;
      return true;
    } catch (error) {
      toastApiError(error, undefined, isEn.value);
      return false;
    } finally {
      submitting.value = false;
    }
  }

  function handleDeposit(): void {
    const links = getLinks();
    toLink(links.depositUrl || DEFAULT_DEPOSIT_URL);
  }

  function handleRules(): void {
    const url = getLinks().rulesUrl;
    if (!url) {
      showToast(copy.value.rulesSoon);
      return;
    }
    toLink(url);
  }

  function handleViewPrize(): void {
    toLink(getLinks().prizeUrl || DEFAULT_PRIZE_URL);
  }

  function handleStickyClick(): void {
    if (showDepositBtn.value) {
      handleDeposit();
      return;
    }
    handleEnroll();
  }

  /** 打开选择弹窗。group=advanced 走 8 万三选一。 */
  function openChoice(nextKey?: string, group: ChoiceGroup = 'entry'): void {
    pendingChoiceGroup.value = group;
    if (group === 'advanced') {
      pendingChoiceKey.value =
        nextKey && isAdvancedChoiceKey(nextKey)
          ? nextKey
          : DEFAULT_ADVANCED_CHOICE_KEY;
    } else {
      pendingChoiceKey.value =
        nextKey && isChoiceKey(nextKey) ? nextKey : DEFAULT_CHOICE_KEY;
    }
    choiceVisible.value = true;
  }

  function openAdvancedChoice(nextKey?: string): void {
    openChoice(nextKey, 'advanced');
  }

  function closeSuccess(): void {
    successVisible.value = false;
  }

  function closeChoice(): void {
    choiceVisible.value = false;
    confirmVisible.value = false;
  }

  /** 选择弹窗点「我選這個」后进入确认弹窗。 */
  function openConfirm(nextKey: ChoiceKey): void {
    pendingChoiceKey.value = nextKey;
    choiceVisible.value = false;
    confirmVisible.value = true;
  }

  /** 「再想想」回到选择弹窗。 */
  function closeConfirm(): void {
    confirmVisible.value = false;
    choiceVisible.value = true;
  }

  function closeChoiceSuccess(): void {
    choiceSuccessVisible.value = false;
  }

  onMounted(() => {
    refreshStatus();
  });

  return {
    loading,
    submitting,
    status,
    enrollment,
    choiceKey,
    advancedChoiceKey,
    showCountdown,
    showChoiceBtn,
    showAdvancedChoiceBtn,
    showDepositBtn,
    showEnrollBtn,
    showAwarded,
    showMissed,
    showTierList,
    awardedImages,
    showSticky,
    stickyText,
    depositEndTime,
    netDepositAmount,
    isInProgress,
    successVisible,
    choiceVisible,
    confirmVisible,
    choiceSuccessVisible,
    pendingChoiceKey,
    pendingChoiceGroup,
    choiceSuccessKey,
    handleEnroll,
    handleUpdateChoice,
    handleDeposit,
    handleRules,
    handleViewPrize,
    handleStickyClick,
    openChoice,
    openAdvancedChoice,
    openConfirm,
    closeSuccess,
    closeChoice,
    closeConfirm,
    closeChoiceSuccess,
  };
}
